import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";

const bodySx = {
  color: "text.secondary",
  lineHeight: 1.75,
} as const;

const resources = [
  {
    title: "Nightscout Survey Report 2025",
    url: "/assets/articles/Nightscout Survey Report 2025.pdf",
  },
  {
    title: "Nightscout Data Commons",
    url: "https://www.openhumans.org/activity/nightscout-data-commons/",
  },
  {
    title: "OpenProject on Open Humans",
    url: "https://www.openhumans.org/activity/open/",
  },
];

const Page = () => {
  const { t } = useTranslation();
  usePageView();

  return (
    <>
      <Seo
        title="Community Research"
        description="Community-driven research initiatives and resources."
      />
      <Box sx={{ backgroundColor: "background.paper", minHeight: "100vh" }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            pt: { xs: 10, md: 14 },
            pb: { xs: 3, md: 4 },
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "primary.main",
                  display: "block",
                  mb: 1.5,
                }}
              >
                Resources
              </Typography>
              <Typography
                component="h1"
                variant="h1"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                  fontSize: { xs: "2.4rem", md: "3.5rem" },
                }}
              >
                {t("Community Research")}
              </Typography>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Stack spacing={4} sx={{ maxWidth: 760 }}>
            <Box
              sx={{
                borderLeft: 3,
                borderColor: "primary.main",
                pl: 2.5,
                py: 0.5,
              }}
            >
              <Typography variant="body2" sx={bodySx}>
                {t("Community-driven research initiatives and resources.")}
              </Typography>
            </Box>

            <Stack spacing={2}>
              {resources.map((resource) => (
                <Link
                  key={resource.url}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                  sx={{
                    display: "block",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    color: "text.primary",
                    fontWeight: 700,
                    lineHeight: 1.5,
                    "&:hover": { borderColor: "primary.main" },
                  }}
                >
                  {resource.title}
                </Link>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Page;
